/**
 * AssignMentorsModal
 *
 * Shows a preview of auto-assigned mentor/participant pairings.
 * Admin can tweak any assignment via dropdown before saving.
 * Each mentor dropdown option shows a live mentee count that updates as changes are made.
 */

import { useEffect, useState, useMemo } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import Select, { SelectProps } from '@cloudscape-design/components/select';
import Badge from '@cloudscape-design/components/badge';
import Alert from '@cloudscape-design/components/alert';
import Link from '@cloudscape-design/components/link';
import { mentorsApi, AssignmentPreviewRow } from '../api/mentors';
import { badgePhotoUrl, phoneToolUrl } from '../lib/externalUrls';

interface AssignMentorsModalProps {
  visible: boolean;
  cohortId: string;
  onDismiss: () => void;
  onSaved: (result?: 'no-changes') => void;
}

interface EditableRow extends AssignmentPreviewRow {
  editedMentorId: string | null;
  editedMentorName: string | null;
}

export function AssignMentorsModal({ visible, cohortId, onDismiss, onSaved }: AssignMentorsModalProps) {
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [mentorOptions, setMentorOptions] = useState<{ mentorId: string; mentorName: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedSuccessfully, setLoadedSuccessfully] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortingColumn, setSortingColumn] = useState<{ sortingField: string }>({ sortingField: 'editedMentorName' });
  const [sortDescending, setSortDescending] = useState(false);

  // Load preview when modal opens
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;

    setError(null);
    setLoadedSuccessfully(false);
    setIsLoading(true);

    mentorsApi.previewAutoAssign(cohortId)
      .then(data => {
        if (!cancelled) {
          setMentorOptions(data.mentors);
          setRows(
            data.assignments.map(a => ({
              ...a,
              editedMentorId: a.mentorId,
              editedMentorName: a.mentorName,
            }))
          );
          setLoadedSuccessfully(true);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err?.response?.message || err?.message || 'Failed to load assignment preview.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [visible, cohortId]);

  const handleMentorChange = (menteeId: string, option: SelectProps.Option) => {
    const mentorId = option.value || null;
    const rawName = mentorId
      ? mentorOptions.find(m => m.mentorId === mentorId)?.mentorName ?? mentorId
      : null;
    setRows(prev => prev.map(r =>
      r.menteeId === menteeId
        ? { ...r, editedMentorId: mentorId, editedMentorName: rawName }
        : r
    ));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const changed = rows.filter(r =>
        r.editedMentorId !== (r.isNew ? null : r.mentorId)
      );
      if (changed.length === 0) {
        onSaved('no-changes');
        onDismiss();
        return;
      }
      const { failed } = await mentorsApi.saveAssignments(
        cohortId,
        changed.map(r => ({ menteeId: r.menteeId, mentorId: r.editedMentorId }))
      );
      if (failed.length > 0) {
        setError(`${failed.length} assignment${failed.length !== 1 ? 's' : ''} could not be saved. Please try again.`);
        return;
      }
      onSaved();
      onDismiss();
    } catch (err: any) {
      setError(err?.response?.message || err?.message || 'Failed to save assignments.');
    } finally {
      setIsSaving(false);
    }
  };

  // Live per-mentor counts — recomputed whenever any assignment changes
  const menteeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of rows) {
      if (r.editedMentorId) {
        counts[r.editedMentorId] = (counts[r.editedMentorId] ?? 0) + 1;
      }
    }
    return counts;
  }, [rows]);

  // Select options with live count in the label
  const selectOptions: SelectProps.Option[] = useMemo(() => [
    { value: '', label: '— Unassigned —' },
    ...mentorOptions.map(m => ({
      value: m.mentorId,
      label: `${m.mentorName} (${menteeCounts[m.mentorId] ?? 0})`,
    })),
  ], [mentorOptions, menteeCounts]);

  // Sort: unassigned always floats to top, then by active sort field
  const sortedRows = [...rows].sort((a, b) => {
    if (!a.editedMentorId && b.editedMentorId) return -1;
    if (a.editedMentorId && !b.editedMentorId) return 1;

    let cmp = 0;
    if (sortingColumn.sortingField === 'menteeName') {
      cmp = a.menteeName.localeCompare(b.menteeName);
    } else {
      cmp = (a.editedMentorName ?? '').localeCompare(b.editedMentorName ?? '')
        || a.menteeName.localeCompare(b.menteeName);
    }
    return sortDescending ? -cmp : cmp;
  });

  const unassignedCount = rows.filter(r => !r.editedMentorId).length;

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header="Review mentor assignments"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={isSaving}>Cancel</Button>
            <Button
              variant="primary"
              loading={isSaving}
              disabled={isLoading || !loadedSuccessfully}
              onClick={handleSave}
            >
              Save assignments
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>
        )}

        {unassignedCount > 0 && !isLoading && (
          <Alert type="warning">
            {unassignedCount} participant{unassignedCount !== 1 ? 's are' : ' is'} not assigned to a mentor.
          </Alert>
        )}

        <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
        <Table
          loading={isLoading}
          loadingText="Loading assignments..."
          trackBy="menteeId"
          sortingDisabled={false}
          sortingColumn={sortingColumn}
          sortingDescending={sortDescending}
          onSortingChange={({ detail }) => {
            setSortingColumn(detail.sortingColumn as { sortingField: string });
            setSortDescending(detail.isDescending ?? false);
          }}
          columnDefinitions={[
            {
              id: 'participant',
              header: 'Participant',
              sortingField: 'menteeName',
              isRowHeader: true,
              cell: (item: EditableRow) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <img
                    src={badgePhotoUrl(item.menteeId)}
                    alt={item.menteeName}
                    style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
                  />
                  <div>
                    <div>{item.menteeName}</div>
                    <div style={{ fontSize: '12px' }}>
                      <Link href={phoneToolUrl(item.menteeId)} external fontSize="body-s">
                        @{item.menteeId}
                      </Link>
                    </div>
                  </div>
                </div>
              ),
            },
            {
              id: 'mentor',
              header: 'Mentor',
              sortingField: 'editedMentorName',
              cell: (item: EditableRow) => (
                <Select
                  selectedOption={
                    item.editedMentorId
                      ? {
                          value: item.editedMentorId,
                          label: `${item.editedMentorName ?? item.editedMentorId} (${menteeCounts[item.editedMentorId] ?? 0})`,
                        }
                      : { value: '', label: '— Unassigned —' }
                  }
                  onChange={({ detail }) => handleMentorChange(item.menteeId, detail.selectedOption)}
                  options={selectOptions}
                  expandToViewport
                />
              ),
            },
            {
              id: 'status',
              header: '',
              cell: (item: EditableRow) =>
                item.isNew && item.editedMentorId
                  ? <Badge color="green">New</Badge>
                  : !item.editedMentorId
                  ? <Badge color="red">Unassigned</Badge>
                  : null,
            },
          ]}
          items={sortedRows}
          empty={
            <Box textAlign="center" color="inherit">
              <Box variant="p">No participants found.</Box>
            </Box>
          }
          variant="borderless"
          wrapLines={false}
        />
        </div>
      </SpaceBetween>
    </Modal>
  );
}


